import { useEffect, useState } from 'react'
import { K, mono } from '../lib/theme'
import { fBps } from '../lib/format'
import { getHeatmap, type Board } from '../lib/api'

const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const DOW_TO_ROW = [6, 0, 1, 2, 3, 4, 5] // JS/Postgres DOW (Sun=0) -> Mon-first row

/** A cell needs a minimum number of samples before it earns a colour. Below
 *  that it is drawn hatched, because a mean of two readings looks identical to
 *  a mean of two hundred once you fill it in. */
const MIN_N = 3

export function Heatmap({ sym, board, setSym }: { sym: string; board: Board | null; setSym: (s: string) => void }) {
  const [cells, setCells] = useState<{ dow: number; hour: number; mean_bps: number; n: number }[]>([])
  const [note, setNote] = useState<string | undefined>()

  useEffect(() => {
    let live = true
    getHeatmap(sym).then((r) => { if (live) { setCells(r.cells); setNote(r.note) } }).catch(() => live && setCells([]))
    return () => { live = false }
  }, [sym])

  const grid = new Map(cells.map((c) => [`${c.dow}:${c.hour}`, c]))
  const withData = cells.filter((c) => c.n >= MIN_N)
  const maxAbs = Math.max(10, ...withData.map((c) => Math.abs(Number(c.mean_bps))))
  const totalSamples = cells.reduce((a, c) => a + c.n, 0)

  const colorFor = (v: number) => {
    const t = Math.min(1, Math.abs(v) / maxAbs)
    const a = 0.12 + t * 0.78
    return v >= 0 ? `rgba(111,227,161,${a.toFixed(3)})` : `rgba(180,140,255,${a.toFixed(3)})`
  }

  return (
    <main style={{ padding: '20px clamp(16px,3vw,28px) 0', maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 16 }}>
        {(board?.tokens ?? []).map((t) => (
          <button key={t.sym} onClick={() => setSym(t.sym)} style={{
            fontFamily: mono, fontSize: 12.5, padding: '6px 10px',
            border: `1px solid ${t.sym === sym ? 'rgba(238,242,255,.6)' : 'rgba(154,163,199,.25)'}`,
            background: t.sym === sym ? 'rgba(238,242,255,.1)' : 'transparent',
            color: t.sym === sym ? K.txt : K.mut, borderRadius: 2, whiteSpace: 'nowrap', flex: 'none', cursor: 'pointer',
          }}>{t.sym}</button>
        ))}
      </div>

      {/* Self-contained frame: title, legend, range and source live inside it so
          the chart stays correct when screenshotted and posted without context. */}
      <section style={{ maxWidth: 1200, margin: '0 auto', border: `1px solid rgba(154,163,199,.24)`, background: K.bg, padding: 'clamp(14px,2.5vw,28px)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '8px 24px', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 320px' }}>
            <div style={{ fontSize: 'clamp(17px,2vw,22px)', fontWeight: 500, lineHeight: 1.25, letterSpacing: '-.01em' }}>
              {sym} token premium to its last real print, by hour of week
            </div>
            <div style={{ fontFamily: mono, fontSize: 12, color: K.mut, marginTop: 6, lineHeight: 1.5 }}>
              Mean basis points · {totalSamples.toLocaleString()} samples · times in ET · Robinhood Chain
            </div>
          </div>
          <div style={{ fontFamily: mono, fontSize: 13, color: K.txt, fontWeight: 500 }}>offbell.app</div>
        </div>

        {(note || withData.length < 24) && (
          <div style={{ marginTop: 14, border: `1px solid rgba(154,163,199,.35)`, padding: '10px 14px', fontSize: 13, lineHeight: 1.45, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: '.06em', color: K.bg, background: K.mut, padding: '2px 6px', flex: 'none' }}>COLLECTING</span>
            <span>{note ?? `Only ${withData.length} of 168 cells have ${MIN_N}+ samples so far. Hatched cells are too thin to colour. The grid fills in as sampling continues.`}</span>
          </div>
        )}

        <div style={{ marginTop: 20, overflowX: 'auto' }}>
          <div style={{ minWidth: 620 }}>
            <div style={{ display: 'flex', gap: 2, marginLeft: 38, marginBottom: 4 }}>
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center', fontFamily: mono, fontSize: 10, color: K.dim, overflow: 'hidden' }}>
                  {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
                </div>
              ))}
            </div>
            {WD.map((d, row) => (
              <div key={d} style={{ display: 'flex', gap: 2, marginTop: 2, alignItems: 'stretch' }}>
                <div style={{ width: 38, flex: 'none', fontFamily: mono, fontSize: 11, color: K.mut, display: 'flex', alignItems: 'center' }}>{d}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const dow = DOW_TO_ROW.indexOf(row)
                  const c = grid.get(`${dow}:${h}`)
                  const n = c?.n ?? 0
                  const v = c ? Number(c.mean_bps) : null
                  const thin = n < MIN_N
                  return (
                    <div key={h}
                      title={`${d} ${String(h).padStart(2, '0')}:00 ET · ${v == null ? 'no samples' : `${fBps(v)} mean, ${n} samples`}`}
                      style={{
                        flex: '1 1 0', minWidth: 0, height: 26,
                        background: v == null || thin ? 'transparent' : colorFor(v),
                        backgroundImage: v != null && thin ? 'repeating-linear-gradient(45deg,rgba(154,163,199,.22) 0 2px,transparent 2px 5px)' : undefined,
                        border: `1px ${v == null ? 'dashed' : 'solid'} rgba(154,163,199,.2)`,
                        boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: mono, fontSize: 10, color: K.txt, overflow: 'hidden',
                      }}>
                      {v != null && !thin ? Math.round(v) : ''}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', marginTop: 16, fontSize: 12, color: K.mut, alignItems: 'center' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 12, height: 12, background: 'rgba(111,227,161,.8)' }} />token above last print</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 12, height: 12, background: 'rgba(180,140,255,.8)' }} />token below last print</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 12, height: 12, backgroundImage: 'repeating-linear-gradient(45deg,rgba(154,163,199,.22) 0 2px,transparent 2px 5px)', border: '1px solid rgba(154,163,199,.2)' }} />under {MIN_N} samples</span>
          <span style={{ fontFamily: mono, color: K.dim }}>scale ±{Math.round(maxAbs)} bps</span>
        </div>
      </section>

      <p style={{ fontSize: 12, color: K.dim, lineHeight: 1.5, margin: '14px auto 0', maxWidth: 1200 }}>
        The frame above carries its own title, legend, range, ticker and source so it stays correct when screenshotted and
        reposted without this page around it.
      </p>
    </main>
  )
}
